import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MenuController } from '@ionic/angular';

import {
  BehaviorSubject,
  combineLatestWith,
  EMPTY,
  filter,
  merge,
  Observable,
  Subscription,
  switchMap,
  take,
} from 'rxjs';

import { HeaderTitleService, RouteHeaderActions } from '@tablesready/frontend-page-badge';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import { PermissionService } from '@tablesready/frontend-permissions';
import { NotificationCenterService } from '@tablesready/frontend-notification-center';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { UserAccountRole } from '@shared/user/enums';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import {
  BillingVisibility,
  FrontendPaidFeatureService,
} from '@tablesready/frontend-paid-feature-alert';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { AuthUser } from '@shared/signup/models';
import { isOnFreePlan } from '@shared/user/functions';
import { SubscriptionState } from '@shared/recurly/enums';

@Component({
  selector: 'frontend-header[routeId]',
  templateUrl: './frontend-header.component.html',
  styleUrls: ['frontend-header.component.scss'],
  standalone: false,
})
export class FrontendHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() routeId = '';
  @Input() hideTitle = false;

  title = '';
  user?: AuthUser;
  showDesktopMenu = false;
  badgeCount?: number = 0;
  actions?: RouteHeaderActions;
  hasBasicPermissions = false;

  isOnFreePlan = false;
  isNotificationCenterAvailable = false;
  hasNotificationCenterFeature = false;
  unreadCount$: Observable<number> = EMPTY;

  private readonly updateSource$ = new BehaviorSubject<string>('');
  private readonly subscription = new Subscription();

  constructor(
    readonly service: HeaderTitleService,
    readonly paidFeatureService: FrontendPaidFeatureService,
    private readonly permissionService: PermissionService,
    private readonly restaurantService: AuthRestaurantService,
    private readonly breakpointService: FrontendBreakpointsService,
    private readonly notificationCenterService: NotificationCenterService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private menuController: MenuController
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['routeId']) {
      this.updateSource$.next(this.routeId);
    }
  }

  ngOnInit(): void {
    const updateSub = this.updateSource$
      .pipe(combineLatestWith(this.service.data$))
      .subscribe(([routeId, data]) => {
        if (!data[routeId]) {
          return;
        }

        this.title = String(data[routeId].title);
        this.badgeCount = data[routeId].badgeCount;
      });

    this.subscription.add(updateSub);

    const authSub = this.restaurantService.selectWithAccount()
      .subscribe((user) => {
        this.user = user;

        if (user?.account && user?.location) {
          this.isOnFreePlan = isOnFreePlan(user);
          this.isNotificationCenterAvailable = this.pricePlansRestrictionsService
            .availableForStarterPlan(user.account.subscriptionType, user.location.recurly);
          this.hasNotificationCenterFeature = !!user.account.features?.notificationCenter && !!user.location.messages?.notificationCenter;
        }
      });
    this.subscription.add(authSub);

    const desktopSub = merge(
      this.breakpointService.observeDesktopMode(),
      this.breakpointService.observeSettingsDesktopMode(),
    )
      .subscribe(() => {
        this.showDesktopMenu = this.routeId === 'settings'
          ? this.breakpointService.isSettingsDesktopMode : this.breakpointService.isDesktopMode;
      });
    this.subscription.add(desktopSub);

    const restSub = this.permissionService.userAccountHasScopesAsync(
      ['user:read', 'manager:read'],
      [UserAccountRole.user, UserAccountRole.admin],
    ).subscribe((permissions) => {
      this.hasBasicPermissions = permissions;
    });
    this.subscription.add(restSub);

    this.unreadCount$ = this.restaurantService.selectWithAccount()
      .pipe(switchMap((user) => {
        if (!user?.account || !user?.location) {
          return EMPTY;
        }

        if (user.location.recurly?.subscription_state === SubscriptionState.paused) {
          return EMPTY;
        }

        const hasBusinessPlan = this.pricePlansRestrictionsService
          .availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);

        return hasBusinessPlan
          ? this.notificationCenterService.totalUnreadCount$
          : this.notificationCenterService.messagesCount$;
      }));
  }

  async openNotificationCenter() {
    if (this.user && !this.isNotificationCenterAvailable) {
      const modalSub = this.paidFeatureService
        .freePlanLocationModalDisplay(this.user, true)
        .pipe(
          take(1),
          filter(({ isConfirmed }) => isConfirmed),
        )
        .subscribe();
      this.subscription.add(modalSub);

      return;
    }

    await this.menuController.open('notification-center');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected readonly icon = AppIcon;
  protected readonly iconSize = AppIconSize;
  protected readonly iconColor = AppIconColor;
  protected readonly BillingVisibility = BillingVisibility;
}
